import { RouteComponentProps, useParams } from '@reach/router';
import { BlobServiceClient } from '@azure/storage-blob';
import React, { useState } from 'react';
import { format } from 'date-fns';
import { uploadBlobs, describeUploadError } from '../../utils/blobUpload';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Modal from '../../components/Modal';
import Table from '../../components/Table';
import FileUpload from '../../components/FileUpload';
import {
    Delivery,
    DeliveryItem,
    useApproveDelivery,
    useCreateDisputeDelivery,
    useGetDelivery,
    UpsertDeliveryFileDTO,
    useUploadDeliveryFile,
} from '../../queries/Delivery';
import { forceDownload, handleWebDownload } from '../../utils/Utils';
import { domain } from '../../d-man';
import { Tab, TabsContainer } from '../../components/Tabs';
import { DisputeMessage, useResolveDeliveryDispute } from '../../queries/Disputes';
import { useGetPermissions } from '../../queries/Permissions';
import Loader from '../../components/Loader';
import Error from '../../components/Error';
import { TrashCan } from '../../components/Icons';
import StatusBadge, { StatusTone } from '../../components/StatusBadge';
import DateFilterInput from '../../components/DateFilterInput';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function DeliveryDetail(props: RouteComponentProps) {
    const { navigate } = props;
    const params = useParams();
    const getId = () => params.id;

    const [openDeliveryFilesModal, setOpenDeliveryFilesModal] = useState(false);
    const [openReturnModal, setOpenReturnModal] = useState(false);
    const [openCreateDisputeModal, setOpenCreateDisputeModal] = useState(false);

    const delivery = useGetDelivery(getId());
    const deliveryApprove = useApproveDelivery(getId());
    const deliveryDisputeCreate = useCreateDisputeDelivery(getId());
    const deliveryDisputeResolve = useResolveDeliveryDispute();
    const [tabs] = useState([
        { label: 'Materials', id: 'Materials' },
        { label: 'Disputes', id: 'Disputes' },
    ] as Tab[]);
    const [selectedTab, setSelectedTab] = useState('Materials');

    const [disputeErrorMessage, setDisputeErrorMessage] = useState('');
    const [message, setMessage] = useState('');
    const [deliveryDisputeDateFrom, setDeliveryDisputeDateFrom] = useState(
        undefined as Date | undefined
    );
    const [deliveryDisputeDateTo, setDeliveryDisputeDateTo] = useState(
        undefined as Date | undefined
    );

    const [deliveryFiles, setDeliveryFiles] = useState([] as File[]);
    const [openUploadFilesModal, setOpenUploadFilesModal] = useState(false);
    const [uploadFileErrorMessage, setUploadFileErrorMessage] = useState('');

    const [isLoading, setIsLoading] = useState(false);

    const getPermissions = useGetPermissions('DELIVERY', getId());

    const uploadFile = useUploadDeliveryFile(getId());

    // REDIRECT IF NOT CORRECT ROLE
    if (
        getPermissions.data &&
        !getPermissions.data?.items.find((p) => p.code === 'Delivery_Show_Delivery_Details_Screen')
            ?.hasAccess &&
        navigate
    ) {
        navigate(`/`);
    }

    const handleReturnModalOpen = () => {
        setOpenReturnModal(true);
    };

    const handleReturnModalClose = () => {
        setOpenReturnModal(false);
    };

    const handleDeliveryFilesModalOpen = () => {
        setOpenDeliveryFilesModal(true);
    };

    const handleDeliveryFilesModalClose = () => {
        setOpenDeliveryFilesModal(false);
    };

    const handleCreateDisputeModalOpen = () => {
        setOpenCreateDisputeModal(true);
    };

    const handleCreateDisputeModalClose = () => {
        setOpenCreateDisputeModal(false);
    };

    const handleUploadFilesModalOpen = () => {
        setOpenUploadFilesModal(true);
    };

    const handleUploadFilesModalClose = () => {
        setOpenUploadFilesModal(false);
    };

    const updateUploadedFiles = (files: File[]) => {
        setDeliveryFiles(files);
    };

    const toOrderDetail = (id?: string) => {
        if (props.navigate) {
            props.navigate(`/orders/${id}`);
        }
    };

    const toCallOffDetail = (id?: string) => {
        if (props.navigate) {
            props.navigate(`/calloffs/${id}`);
        }
    };

    const handleExportDownload = async (endpoint: string) => {
        setIsLoading(true);
        const [, data, response] = await domain
            .get(endpoint, {
                requestConfig: {
                    responseType: 'blob', // important
                },
            })
            .execute();
        if (!data) return;
        handleWebDownload(data, response);
        setIsLoading(false);
        delivery.reset();
    };

    const getStatus = (deliveryStatus: Delivery) => {
        if (deliveryStatus.exportedAt !== null || deliveryStatus.exportedBy !== null) {
            return 'Exported';
        }
        if (deliveryStatus.approvedAt !== null || deliveryStatus.approvedBy !== null) {
            return 'Approved';
        }
        return 'Draft';
    };

    const getDisputeResolved = (disputeMessage: DisputeMessage) =>
        !!delivery?.data?.disputes
            ?.filter((i) => disputeMessage.deliveryDisputeId === i.id)
            ?.every((i) => i.resolvedAt && i.resolvedBy);

    const getDisputeStatusTone = (disputeMessage: DisputeMessage): StatusTone =>
        getDisputeResolved(disputeMessage) ? 'success' : 'warning';

    const postCreateDispute = async () => {
        if (!message) {
            setDisputeErrorMessage('Please capture all the fields above');
        } else {
            setIsLoading(true);
            let deliveryDisputeId: string | undefined;
            if (
                delivery &&
                delivery.data &&
                delivery.data.disputes &&
                delivery.data.disputes.some((i) => !i.resolvedAt && !i.resolvedBy)
            ) {
                deliveryDisputeId = delivery.data.disputes.filter(
                    (i) => !i.resolvedAt && !i.resolvedBy
                )[0].id;
            }

            await deliveryDisputeCreate.execute({ message, deliveryDisputeId });
            if (deliveryDisputeCreate.error) {
                // Display create order error here
                setDisputeErrorMessage(deliveryDisputeCreate.error);
            } else {
                handleCreateDisputeModalClose();
            }
            setIsLoading(false);
            setMessage('');
            delivery.reset();
        }
    };

    const handleDownload = async (file: {
        id?: string | undefined;
        name?: string | undefined;
        requisitionId?: string | null | undefined;
        deliveryId?: string | null | undefined;
        returnId?: string | null | undefined;
        blobId?: string | null | undefined;
        uploadedAt?: string | null | undefined;
    }) => {
        if (file && file.blobId) {
            const sasUrl = process.env.REACT_APP_AZURE_BLOB_CONNECTIONSTRING || '';
            const blobServiceClient = new BlobServiceClient(sasUrl);
            const containerClient = blobServiceClient.getContainerClient('uploads');
            await containerClient.createIfNotExists();

            const blockBlobClient = containerClient.getBlockBlobClient(file.blobId);
            // eslint-disable-next-line no-await-in-loop
            const uploadBlobResponse = await blockBlobClient.download();
            if (uploadBlobResponse.requestId) {
                const blobby = (await uploadBlobResponse.blobBody) || new Blob();
                const url = window.URL.createObjectURL(new Blob([blobby]));

                forceDownload(url, file.name || '');
            }
        }
    };

    const postUploadFile = async () => {
        setUploadFileErrorMessage('');

        if (deliveryFiles.length <= 0) {
            setUploadFileErrorMessage('You must upload at least 1 file');
            return;
        }

        setIsLoading(true);
        try {
            const blobs = await uploadBlobs(deliveryFiles);
            const data: UpsertDeliveryFileDTO = { blobs };

            // The error is read from what execute() returns. Reading
            // uploadFile.error straight after awaiting gave the value captured
            // when this function was created, so a failed upload took the
            // success path and the dialog closed as though it had worked.
            const [error] = await uploadFile.execute(data);
            if (error) {
                setUploadFileErrorMessage(error);
                return;
            }

            handleUploadFilesModalClose();
            delivery.reset();
        } catch (error) {
            // Nothing caught these before, so a failed upload left the dialog
            // spinning with no message and no way out but a reload.
            setUploadFileErrorMessage(describeUploadError(error));
        } finally {
            // In a finally so every path clears it, including the failures.
            setIsLoading(false);
        }
    };

    const deleteFile = async (id: string) => {
        const remove = domain.delete(`/files/${id}`);
        await remove.execute();
        delivery.reset();
    };

    const getLineItemNumber = (row: DeliveryItem) => {
        if (!row?.id) return '';
        const lineItemIndex = delivery?.data?.items?.findIndex((item) => {
            if (!item.lineItem || !row.lineItem) return '';
            return item?.lineItem.id === row?.lineItem.id;
        });

        return !lineItemIndex || lineItemIndex === -1 ? 1 : lineItemIndex + 1;
    };

    const renderDelivery = () => {
        if (!delivery || delivery.loading) {
            return <Loader message="Fetching Delivery from server" />;
        }
        if (delivery.error) {
            return (
                <Error message={`Failed to fetch Delivery from the server (${delivery.error})`} />
            );
        }
        if (delivery.data) {
            const { note, createdAt, orderId, callOffId, payByDate } = delivery.data;
            return (
                <>
                    <div className="flex flex-wrap items-center justify-between gap-4 p-4 border-b border-border">
                        <div className="text-lg font-semibold text-foreground">
                            Delivery Information
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {orderId && (
                                <Button onClick={() => toOrderDetail(orderId)}>Go To Order</Button>
                            )}
                            {callOffId && (
                                <Button onClick={() => toCallOffDetail(callOffId)}>
                                    Go To CallOff
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="p-4">
                        <div className="flex flex-wrap gap-4">
                            <Input label="Delivery Note" value={note} type="text" disabled />
                            <Input
                                label="Created"
                                value={createdAt ? format(new Date(createdAt), 'yyyy-MM-dd') : ''}
                                type="text"
                                disabled
                            />
                            <Input
                                label="Status"
                                value={getStatus(delivery.data)}
                                type="text"
                                disabled
                            />
                            <Input
                                label="Pay by"
                                value={payByDate ? format(new Date(payByDate), 'yyyy-MM-dd') : ''}
                                type="text"
                                disabled
                            />
                        </div>
                    </div>
                    <div className="border-t border-border mt-4" />
                    <div className="flex flex-wrap gap-3 p-4">
                        {delivery.data?.files && delivery.data?.files.length > 0 && (
                            <Button onClick={handleDeliveryFilesModalOpen}>Delivery Files</Button>
                        )}
                        {delivery.data?.returnFiles && delivery.data?.returnFiles.length > 0 && (
                            <Button onClick={handleReturnModalOpen}>Returns</Button>
                        )}
                        {getPermissions.data?.items.find((p) => p.code === 'Delivery_Approval')
                            ?.hasAccess &&
                            delivery.data.approvedAt === null &&
                            delivery.data.approvedBy === null && (
                                <Button
                                    onClick={() => {
                                        deliveryApprove.execute().then(delivery.reset);
                                    }}
                                >
                                    Approve
                                </Button>
                            )}
                        {getPermissions.data?.items.find((p) => p.code === 'Delivery_Upload_Files')
                            ?.hasAccess &&
                            delivery.data && (
                                <Button onClick={handleUploadFilesModalOpen}>Upload Files</Button>
                            )}
                        {((delivery.data.exportedAt == null && delivery.data.exportedBy == null) ||
                            getPermissions.data?.items.find(
                                (p) => p.code === 'Delivery_Export_Override'
                            )?.hasAccess) &&
                            delivery.data.approvedAt !== null &&
                            delivery.data.approvedBy !== null && (
                                <Button
                                    onClick={() =>
                                        handleExportDownload(
                                            `/deliveries/${delivery.data?.id}/export`
                                        )
                                    }
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Loading' : 'Export'}
                                </Button>
                            )}
                    </div>
                </>
            );
        }
        return null;
    };

    return (
        <>
            {!getPermissions.data && <Loader message="Loading delivery..." />}
            {getPermissions.data && delivery.data && (
                <div className="">
                    <Modal
                        title="Delivery Files"
                        subtitle="Download delivery files"
                        onClose={handleDeliveryFilesModalClose}
                        isOpen={openDeliveryFilesModal}
                    >
                        <div className="flex justify-between">
                            <Table<{
                                id?: string | undefined;
                                name?: string | undefined;
                                requisitionId?: string | null | undefined;
                                deliveryId?: string | null | undefined;
                                returnId?: string | null | undefined;
                                blobId?: string | null | undefined;
                                uploadedAt?: string | null | undefined;
                            }>
                                loading={delivery.loading && 'Fetching Files from server'}
                                error={
                                    delivery.error &&
                                    `Failed to fetch File list from the server (${delivery.error})`
                                }
                                data={delivery.data?.files?.filter((file) => !file.isDeleted) || []}
                                // onClick={(file) => handleDownload(file)}
                                columns={[
                                    {
                                        key: 'name',
                                        title: 'File Name',
                                        onClick: (file) => handleDownload(file),
                                    },
                                    {
                                        key: 'uploadedAt',
                                        title: 'Created At',
                                        render: (file) =>
                                            format(new Date(file.uploadedAt || ''), 'yyyy-MM-dd'),
                                        onClick: (file) => handleDownload(file),
                                    },
                                    {
                                        key: 'delete',
                                        title: '',
                                        render: (file) => (
                                            <TrashCan
                                                className="cursor-pointer pl-2"
                                                id={file.id}
                                                onClick={() => deleteFile(file.id || '')}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </div>
                        <div />
                    </Modal>
                    <Modal title="Return" onClose={handleReturnModalClose} isOpen={openReturnModal}>
                        <div className="mb-5">
                            <Input
                                label="Note"
                                value={delivery.data?.returnNote}
                                type="text"
                                disabled
                            />
                        </div>
                        <div className="flex justify-between">
                            <Table<{
                                id?: string | undefined;
                                name?: string | undefined;
                                requisitionId?: string | null | undefined;
                                deliveryId?: string | null | undefined;
                                returnId?: string | null | undefined;
                                blobId?: string | null | undefined;
                                uploadedAt?: string | null | undefined;
                            }>
                                loading={delivery.loading && 'Fetching Files from server'}
                                error={
                                    delivery.error &&
                                    `Failed to fetch File list from the server (${delivery.error})`
                                }
                                data={delivery.data?.returnFiles || []}
                                onClick={(file) => handleDownload(file)}
                                columns={[
                                    {
                                        key: 'name',
                                        title: 'File Name',
                                    },
                                    {
                                        key: 'uploadedAt',
                                        title: 'Created At',
                                        render: (file) =>
                                            format(new Date(file.uploadedAt || ''), 'yyyy-MM-dd'),
                                    },
                                ]}
                            />
                        </div>
                        <div />
                    </Modal>
                    {delivery.data && (
                        <Modal
                            title="Add Dispute"
                            // subtitle={`${order.data.number.toString().padStart(6, '0')}`}
                            onClose={handleCreateDisputeModalClose}
                            isOpen={openCreateDisputeModal}
                            onAction={postCreateDispute}
                            actionText={isLoading ? 'Loading' : ''}
                        >
                            <div className="ml-5">
                                <div>
                                    <Input
                                        label="Dispute Message"
                                        placeholder="Add Message Here"
                                        value={message}
                                        type="text"
                                        onChange={(event) => setMessage(event.target.value)}
                                    />
                                </div>
                                <div className="flex mb-5 text-sm text-danger">
                                    {disputeErrorMessage}
                                </div>
                            </div>
                        </Modal>
                    )}
                    <Modal
                        title="Upload Files"
                        onClose={handleUploadFilesModalClose}
                        isOpen={openUploadFilesModal}
                        onAction={postUploadFile}
                        actionText={isLoading ? 'Loading...' : ''}
                    >
                        <div className="flex justify-between">
                            <div className="ml-5">
                                <div className="mb-5">
                                    <FileUpload updateFilesCb={updateUploadedFiles} multiple />
                                </div>
                            </div>
                        </div>
                        <div className="flex mb-5 text-sm text-danger">
                            {uploadFileErrorMessage}
                        </div>
                    </Modal>

                    <div className="bg-surface border border-border rounded-xl m-4">
                        <div className="p-5">
                            <TabsContainer
                                selectedTab={selectedTab}
                                options={tabs}
                                onClick={setSelectedTab}
                            >
                                {selectedTab === 'Materials' && (
                                    <div className="w-full h-full mt-4">
                                        <Table<DeliveryItem>
                                            loading={
                                                delivery.loading &&
                                                'Fetching Deliveries from server'
                                            }
                                            error={
                                                delivery.error &&
                                                `Failed to fetch Delivery list from the server (${delivery.error})`
                                            }
                                            data={delivery.data?.items}
                                            columns={[
                                                {
                                                    key: '',
                                                    title: 'No',
                                                    render: (deliveryItem) => {
                                                        return getLineItemNumber(deliveryItem);
                                                    },
                                                },
                                                {
                                                    key: 'description',
                                                    title: 'Description',
                                                    render: (deliveryItem) => {
                                                        const { description } =
                                                            deliveryItem.lineItem || {};
                                                        return description;
                                                    },
                                                },
                                                {
                                                    key: 'quantity',
                                                    title: 'Delivered',
                                                },
                                                {
                                                    key: 'returnedQuantity',
                                                    title: 'Returned',
                                                },
                                                {
                                                    key: 'costCode',
                                                    title: 'Cost Code',
                                                    render: (deliveryItem) => {
                                                        const { costCode } =
                                                            deliveryItem.lineItem || {};
                                                        return costCode?.name;
                                                    },
                                                },
                                            ]}
                                        />
                                    </div>
                                )}
                                {selectedTab === 'Disputes' && (
                                    <div className="space-y-4 mt-4">
                                        <div className="flex flex-wrap items-end justify-between gap-4">
                                            <div className="flex flex-wrap gap-4">
                                                <DateFilterInput
                                                    label="Date From"
                                                    selected={deliveryDisputeDateFrom}
                                                    onChange={setDeliveryDisputeDateFrom}
                                                />
                                                <DateFilterInput
                                                    label="Date To"
                                                    selected={deliveryDisputeDateTo}
                                                    onChange={setDeliveryDisputeDateTo}
                                                />
                                            </div>
                                            <div className="flex flex-wrap gap-3">
                                                {delivery.data &&
                                                    delivery.data.disputes &&
                                                    delivery.data.disputes.some(
                                                        (i) => !i.resolvedAt && !i.resolvedBy
                                                    ) && (
                                                        <Button
                                                            onClick={() =>
                                                                deliveryDisputeResolve
                                                                    .execute({
                                                                        deliveryDisputeId:
                                                                            delivery.data?.disputes?.filter(
                                                                                (i) =>
                                                                                    !i.resolvedAt &&
                                                                                    !i.resolvedBy
                                                                            )[0].id || '',
                                                                    })
                                                                    .then(delivery.reset)
                                                            }
                                                            disabled={isLoading}
                                                        >
                                                            {isLoading ? 'Loading' : 'Resolve'}
                                                        </Button>
                                                    )}
                                                {getPermissions.data?.items.find(
                                                    (p) => p.code === 'Delivery_Dispute'
                                                )?.hasAccess && (
                                                    <Button
                                                        onClick={() =>
                                                            handleCreateDisputeModalOpen()
                                                        }
                                                    >
                                                        Add Dispute
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        {delivery.data && delivery.data.disputes && (
                                            <div className="space-y-3">
                                                <div className="text-lg font-semibold text-foreground">
                                                    Disputes
                                                </div>
                                                {/* Legend */}
                                                <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-2.5 px-4 py-3 bg-surface-muted border border-border rounded-lg">
                                                    <span className="text-xs font-semibold text-foreground-muted uppercase tracking-wide">
                                                        Status
                                                    </span>
                                                    <StatusBadge label="Open" tone="warning" />
                                                    <StatusBadge label="Resolved" tone="success" />
                                                </div>
                                                <Table<DisputeMessage>
                                                    data={
                                                        delivery.data.disputes
                                                            .filter(
                                                                (d) =>
                                                                    (!deliveryDisputeDateFrom ||
                                                                        new Date(
                                                                            Date.parse(
                                                                                d.createdAt || '0'
                                                                            )
                                                                        ) >=
                                                                            deliveryDisputeDateFrom) &&
                                                                    (!deliveryDisputeDateTo ||
                                                                        new Date(
                                                                            Date.parse(
                                                                                d.createdAt || '0'
                                                                            )
                                                                        ) <= deliveryDisputeDateTo)
                                                            )
                                                            .map((l) => l.disputeMessages)
                                                            .reduce(
                                                                (a, b) => a?.concat(b || []),
                                                                []
                                                            ) || []
                                                    }
                                                    columns={[
                                                        {
                                                            key: 'createdAt',
                                                            title: 'Created At',
                                                            render: (disputeMessage) =>
                                                                format(
                                                                    new Date(
                                                                        disputeMessage.createdAt ||
                                                                            ''
                                                                    ),
                                                                    'yyyy-MM-dd hh:mm:ss'
                                                                ),
                                                        },
                                                        {
                                                            key: 'message',
                                                            title: 'Message',
                                                        },
                                                        {
                                                            key: 'active',
                                                            title: 'Resolved',
                                                            render: (disputeMessage) => (
                                                                <StatusBadge
                                                                    label={
                                                                        getDisputeResolved(
                                                                            disputeMessage
                                                                        )
                                                                            ? 'Resolved'
                                                                            : 'Open'
                                                                    }
                                                                    tone={getDisputeStatusTone(
                                                                        disputeMessage
                                                                    )}
                                                                />
                                                            ),
                                                        },
                                                    ]}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </TabsContainer>
                        </div>
                    </div>
                    <div className="bg-surface border border-border rounded-xl m-4">
                        {renderDelivery()}
                    </div>
                </div>
            )}
        </>
    );
}
